import { Alert, Box, Stack } from "@mui/material";
import { useReducer } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import adminApi from "../../../api/adminApi";
import useClientContext from "../../../context/useClientContext";
import useUserContext from "../../../context/useUserContext";
import PassthroughLogo from "../../../images/passthrough.png";
import IntegrationPageContainer from "../integration/IntegrationPageContainer";
import PassthroughConnectionActions from "./PassthroughConnectionActions";
import PassthroughSetup from "./PassthroughSetup";
import PassthroughTabs from "./PassthroughTabs";
import { getInitialState, reducer } from "./passthroughPageState";

const makeTestConnection = withErrorHandling(adminApi.makePassthroughTestConnection);
const toggleIntegration = withErrorHandling(adminApi.togglePassthroughIntegration);
const updateConfiguration = withErrorHandling(adminApi.updatePassthroughConfiguration);

const PassThroughPage = () => {
  usePageTitle("Passthrough");

  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { refreshUserData } = useUserContext();
  const { passthroughApp, hasPermissions } = useClientContext();
  const [state, dispatch] = useReducer(reducer, getInitialState());

  const canManagePassthroughIntegration = hasPermissions(["ManageClientIntegrations"]);

  const [connectionStateResult, connectionStateFetchError] = useFetch(adminApi.getPassthroughConnectionState, {
    onSetData: (data) => dispatch({ type: "SetConnectionState", payload: { ...data } }),
  });

  const [configurationResult, configurationFetchError] = useFetch(adminApi.getPassthroughConfiguration, {
    onSetData: (data) => dispatch({ type: "SetConfiguration", payload: { ...data } }),
  });

  const handleTestConnection = async () => {
    dispatch({ type: "SetActiveProcess", payload: "testingConnection" });
    const [testConnectionResponse, error] = await makeTestConnection();
    if (!error && testConnectionResponse.isSuccess) {
      dispatch({
        type: "SetAlert",
        payload: {
          severity: "info",
          message: "The test connection was successful. You can view all logs in the Logs tab.",
        },
      });
    } else {
      dispatch({
        type: "SetAlert",
        payload: {
          severity: "error",
          message: "An error occurred while testing the connection. You can view all logs in the Logs tab.",
        },
      });
    }
    dispatch({ type: "ResetActiveProcess" });
  };

  const handleSaveConfiguration = async () => {
    if (!state.configuration) return;
    dispatch({ type: "SetActiveProcess", payload: "updatingConfiguration" });
    const [, error] = await updateConfiguration(state.configuration);
    if (!error) {
      sendNotification("The configuration was saved successfully.");
      dispatch({ type: "ResetConfigurationDirty" });
    } else {
      sendNotificationError("An error occurred while saving the configuration.");
    }
    dispatch({ type: "ResetActiveProcess" });
  };

  const handleToggleIntegration = async (enabled: boolean) => {
    dispatch({ type: "SetActiveProcess", payload: "togglingIntegration" });

    const [toggleConnectionResponse, error] = await toggleIntegration({ enabled });
    if (!error && enabled === toggleConnectionResponse.enabled) {
      dispatch({ type: "SetConnectionState", payload: { ...toggleConnectionResponse } });
      sendNotification(`The integration was successfully ${enabled ? "enabled" : "disabled"}.`);
      refreshUserData();
    } else {
      sendNotificationError(`An error occurred while ${enabled ? "enabling" : "disabling"} the integration.`);
    }
    dispatch({ type: "ResetActiveProcess" });
  };

  if (connectionStateFetchError || configurationFetchError) {
    return <DataLoadingFailed title="Could not load data" />;
  }

  if (!connectionStateResult || !configurationResult) {
    return <InlineLoader />;
  }

  return (
    <IntegrationPageContainer
      title="Passthrough"
      logoSrc={PassthroughLogo}
      overviewText="Intelligent sub docs and KYC/AML for private markets."
      appStatus={passthroughApp?.status}
      maxWidth="lg"
    >
      <Box sx={{ position: "relative" }}>
        <Stack spacing={3}>
          <PassthroughSetup lastChangedBy={state.lastChangedBy} lastChangedOn={state.lastChangedOn} />
          <PassthroughConnectionActions
            integrationEnabled={state.enabled}
            onTestConnection={handleTestConnection}
            onToggleIntegration={handleToggleIntegration}
            testConnectionProcessing={state.activeProcess === "testingConnection"}
            toggleIntegrationProcessing={state.activeProcess === "togglingIntegration"}
            disabled={state.isConfigurationDirty || !canManagePassthroughIntegration || !state.configuration.apiKey}
          />
          {state.alertMessage && (
            <Alert severity={state.alertMessage.severity} onClose={() => dispatch({ type: "SetAlert" })}>
              {state.alertMessage.message}
            </Alert>
          )}
          <PassthroughTabs
            state={state}
            disabled={!canManagePassthroughIntegration}
            onConfigurationChange={(data) => dispatch({ type: "UpdateConfiguration", payload: data })}
            onConfigurationSave={handleSaveConfiguration}
          />
        </Stack>
        {state.activeProcess && <InlineLoader overlay sx={{ pointerEvents: "auto", top: 0, left: 0, zIndex: 2 }} />}
      </Box>
    </IntegrationPageContainer>
  );
};

export default PassThroughPage;
